/**
 * SimpleSimulationControls - Kompakte Benutzeroberfläche für Simulationsparameter-Steuerung
 *
 * Diese vereinfachte Komponente stellt eine kompakte Oberfläche für die Konfiguration
 * von Simulationsparametern bereit, optimiert für Schritt 3 und 4 ohne detaillierte
 * Informationsanzeige.
 *
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 * @since 2.1.0
 */

import React from 'react';
import { Box, ColumnLayout, Header, Select, SpaceBetween } from '@cloudscape-design/components';
import { SteuerParameter } from '@/types';
// Inline label helpers (previously from labelHelpers.ts)
const getCrisisDefinitionLabel = (value: string): string => {
  switch (value) {
    case 'negative-return': return 'Krise bei negativer Jahresrendite';
    case 'kapitalindex-drawdown': return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
    default: return 'Krise bei negativer Jahresrendite';
  }
};

const getInflationOptionLabel = (value: string, steuerParameter?: { inflationsrate?: number }): string => {
  switch (value) {
    case 'keine-inflation': return 'Keine Inflation (0%)';
    case 'parameter-inflation': return `Inflation mit Prozentwert aus Eingabeparameter (${steuerParameter?.inflationsrate || 2.0}%)`;
    case 'historische-inflation': return 'Inflation als Zufallswert aus historische Inflation';
    case 'statistisch-inflation': return 'Inflation als Zufallswert basierend auf Mittelwert und Standardabweichung';
    default: return 'Keine Inflation (0%)';
  }
};

const getInvestmentReturnOptionLabel = (value: string, steuerParameter?: { renditeInvestitionstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'konstante-rendite': return `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
    case 'historische-rendite': return 'Rendite als Zufallswert aus MSCI World historischen Renditen';
    case 'statistisch-rendite': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Renditen des MSCI World';
    case 'historische-garch': return 'Realistische Krisen-Modellierung: Historische Marktdaten + intelligente Volatilitäts-Cluster (inkl. 2008, 2000, COVID)';
    case 'statistisch-garch': return 'Theoretische Modellierung: Statistische Verteilung + zeitvariable Volatilität (glatte Übergänge, kontrollierbar)';
    default: return `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
  }
};

const getTagesgeldOptionLabel = (value: string, steuerParameter?: { zinssatzVerbrauchstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'parameter-rendite': return `Rendite aus Parameterwert (${steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`;
    case 'historisch-tagesgeld': return 'Rendite als Zufallswert aus historischen Tagesgeld-Zinssätzen';
    case 'statistisch-tagesgeld': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Tagesgeld-Zinssätze';
    default: return 'Keine Rendite (0%)';
  }
};

const getZinstopfOptionLabel = (value: string, steuerParameter?: { zinssatzZinstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'parameter-rendite': return `Rendite aus Parameterwert (${steuerParameter?.zinssatzZinstopf || 3.0}%)`;
    case 'historisch-zinstopf': return 'Rendite als Zufallswert aus historischen Zinstopf-Performances';
    case 'statistisch-zinstopf': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Zinstopf-Performances';
    default: return 'Keine Rendite (0%)';
  }
};

/**
 * Props für die SimpleSimulationControls-Komponente
 */
interface SimpleSimulationControlsProps {
  /** Aktuelle Steuerparameter mit Grundeinstellungen und Zinssätzen */
  steuerParameter: SteuerParameter;

  /** Aktuell gewählte Krisendefinition */
  crisisDefinition: string;

  /** Aktuell gewählte Inflations-Berechnungsstrategie */
  inflationOption: string;

  /** Aktuell gewählte Investitionsrendite-Berechnungsstrategie */
  investmentReturnOption: string;

  /** Aktuell gewählte Tagesgeld-Zinssatz-Berechnungsstrategie */
  tagesgeldOption: string;

  /** Aktuell gewählte Zinstopf-Rendite-Berechnungsstrategie */
  zinstopfOption: string;

  /** Callback für Änderung der Krisendefinition */
  onCrisisDefinitionChange: (value: string) => void;

  /** Callback für Änderung der Inflations-Berechnungsstrategie */
  onInflationOptionChange: (value: string) => void;

  /** Callback für Änderung der Investitionsrendite-Berechnungsstrategie */
  onInvestmentReturnOptionChange: (value: string) => void;

  /** Callback für Änderung der Tagesgeld-Berechnungsstrategie */
  onTagesgeldOptionChange: (value: string) => void;

  /** Callback für Änderung der Zinstopf-Berechnungsstrategie */
  onZinstopfOptionChange: (value: string) => void;
}

/**
 * SimpleSimulationControls - Kompakte Simulationsparameter-Konfiguration
 */
export const SimpleSimulationControls: React.FC<SimpleSimulationControlsProps> = ({
  steuerParameter,
  crisisDefinition,
  inflationOption,
  investmentReturnOption,
  tagesgeldOption,
  zinstopfOption,
  onCrisisDefinitionChange,
  onInflationOptionChange,
  onInvestmentReturnOptionChange,
  onTagesgeldOptionChange,
  onZinstopfOptionChange,
}) => {
  return (
    <Box>
      <ColumnLayout columns={2} borders="vertical">
        {/* First Column */}
        <SpaceBetween direction="vertical" size="m">
          {/* Inflation */}
          <Box>
            <Header variant="h3">Berücksichtigung von Inflation</Header>
            <Select
              selectedOption={
                inflationOption
                  ? {
                      label: getInflationOptionLabel(inflationOption, steuerParameter),
                      value: inflationOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onInflationOptionChange(detail.selectedOption?.value || 'keine-inflation')
              }
              options={[
                {
                  label: 'Keine Inflation (0%)',
                  value: 'keine-inflation',
                },
                {
                  label: `Inflation mit Prozentwert aus Eingabeparameter (${steuerParameter?.inflationsrate || 2.0}%)`,
                  value: 'parameter-inflation',
                },
                {
                  label: 'Inflation als Zufallswert aus historische Inflation',
                  value: 'historische-inflation',
                },
                {
                  label:
                    'Inflation als Zufallswert basierend auf Mittelwert und Standardabweichung',
                  value: 'statistisch-inflation',
                },
              ]}
              expandToViewport
            />
          </Box>

          {/* Verbrauchstopf Returns */}
          <Box>
            <Header variant="h3">Rendite des Verbrauchstopfs</Header>
            <Select
              selectedOption={
                tagesgeldOption
                  ? {
                      label: getTagesgeldOptionLabel(tagesgeldOption, steuerParameter),
                      value: tagesgeldOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onTagesgeldOptionChange(detail.selectedOption?.value || 'parameter-rendite')
              }
              options={[
                {
                  label: 'Keine Rendite (0%)',
                  value: 'keine-rendite',
                },
                {
                  label: `Rendite aus Parameterwert (${steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`,
                  value: 'parameter-rendite',
                },
                {
                  label: 'Rendite als Zufallswert aus historischen Tagesgeld-Zinssätzen',
                  value: 'historisch-tagesgeld',
                },
                {
                  label:
                    'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Tagesgeld-Zinssätze',
                  value: 'statistisch-tagesgeld',
                },
              ]}
              expandToViewport
            />
          </Box>
        </SpaceBetween>

        {/* Second Column */}
        <SpaceBetween direction="vertical" size="m">
          {/* Zinstopf Returns */}
          <Box>
            <Header variant="h3">Rendite des Zinstopfs</Header>
            <Select
              selectedOption={
                zinstopfOption
                  ? {
                      label: getZinstopfOptionLabel(zinstopfOption, steuerParameter),
                      value: zinstopfOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onZinstopfOptionChange(detail.selectedOption?.value || 'parameter-rendite')
              }
              options={[
                {
                  label: 'Keine Rendite (0%)',
                  value: 'keine-rendite',
                },
                {
                  label: `Rendite aus Parameterwert (${steuerParameter?.zinssatzZinstopf || 3.0}%)`,
                  value: 'parameter-rendite',
                },
                {
                  label: 'Rendite als Zufallswert aus historischen Zinstopf-Performances',
                  value: 'historisch-zinstopf',
                },
                {
                  label:
                    'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Zinstopf-Performances',
                  value: 'statistisch-zinstopf',
                },
              ]}
              expandToViewport
            />
          </Box>

          {/* Investitionstopf Returns */}
          <Box>
            <Header variant="h3">Rendite des Investitionstopf</Header>
            <Select
              selectedOption={
                investmentReturnOption
                  ? {
                      label: getInvestmentReturnOptionLabel(
                        investmentReturnOption,
                        steuerParameter
                      ),
                      value: investmentReturnOption,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onInvestmentReturnOptionChange(detail.selectedOption?.value || 'konstante-rendite')
              }
              options={[
                {
                  label: 'Keine Rendite (0%)',
                  value: 'keine-rendite',
                },

                {
                  label: `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`,
                  value: 'konstante-rendite',
                },
                {
                  label: 'Rendite als Zufallswert aus MSCI World historischen Renditen',
                  value: 'historische-rendite',
                },
                {
                  label:
                    'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Renditen des MSCI World',
                  value: 'statistisch-rendite',
                },
                {
                  label:
                    'Realistische Krisen-Modellierung: Historische Marktdaten + intelligente Volatilitäts-Cluster (inkl. 2008, 2000, COVID)',
                  value: 'historische-garch',
                },
                {
                  label:
                    'Theoretische Modellierung: Statistische Verteilung + zeitvariable Volatilität (glatte Übergänge, kontrollierbar)',
                  value: 'statistisch-garch',
                },
              ]}
              expandToViewport
            />
          </Box>

          {/* Crisis Definition */}
          <Box>
            <Header variant="h3">Definition einer Krise</Header>
            <Select
              selectedOption={
                crisisDefinition
                  ? {
                      label: getCrisisDefinitionLabel(crisisDefinition),
                      value: crisisDefinition,
                    }
                  : null
              }
              onChange={({ detail }) =>
                onCrisisDefinitionChange(detail.selectedOption?.value || 'negative-return')
              }
              options={[
                {
                  label: 'Krise bei negativer Jahresrendite',
                  value: 'negative-return',
                },
                {
                  label: 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung',
                  value: 'kapitalindex-drawdown',
                },
              ]}
              expandToViewport
            />
          </Box>
        </SpaceBetween>
      </ColumnLayout>
    </Box>
  );
};

/**
 * Hilfsfunktionen für Optionsbeschriftungen
 */
